// User Management Types for FlahaFAST Client

export interface User {
  id: string;
  email: string;
  name?: string | null;
  company?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  username?: string | null;
  role?: 'USER' | 'ADMIN' | 'MODERATOR' | string;
  createdAt: string;
  updatedAt: string;
  lastLogin?: string | null;
  emailVerified: boolean;
  isActive?: boolean;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
  company?: string;
  startTrial?: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  newPassword: string;
}

export interface ProfileUpdateRequest {
  name?: string;
  company?: string;
}

export interface AuthResponse {
  user: User;
  token: string;
  refreshToken: string;
  subscription?: Subscription;
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  errors?: Array<{ field: string; message: string }>;
}

// Subscription Types
export enum SubscriptionTier {
  FREE = 'FREE',
  PROFESSIONAL = 'PROFESSIONAL',
  ENTERPRISE = 'ENTERPRISE'
}

export enum SubscriptionStatus {
  ACTIVE = 'active',
  TRIALING = 'trialing',
  PAST_DUE = 'past_due',
  CANCELLED = 'cancelled',
  UNPAID = 'unpaid'
}

export interface Subscription {
  id: string;
  userId: string;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  trialEnd?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UsageStats {
  userId: string;
  period: string; // YYYY-MM format
  recipesCreated: number;
  waterProfilesCreated: number;
  formulationsCreated: number;
  customSaltsCreated: number;
  storageUsedMB: number;
  apiCalls: number;
  lastUpdated: string;
}

export interface UserPreferences {
  id: string;
  userId: string;
  defaultVolumeUnit: 'L' | 'gal' | 'm³';
  defaultMassUnit: 'g' | 'kg' | 'oz' | 'lb';
  defaultConcentrationUnit: 'ppm' | 'mg/L' | 'mmol/L';
  theme: 'light' | 'dark' | 'auto';
  language: string;
  emailNotifications: {
    trialReminders: boolean;
    usageLimits: boolean;
    productUpdates: boolean;
    securityAlerts: boolean;
  };
  privacySettings: {
    shareUsageData: boolean;
    allowAnalytics: boolean;
  };
  createdAt: string;
  updatedAt: string;
}

// Feature and Limit Types
export interface FeatureFlags {
  versionHistory: boolean;
  csvImport: boolean;
  csvExport: boolean;
  teamCollaboration: boolean;
  advancedAnalytics: boolean;
  apiAccess: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
}

export interface TierLimits {
  maxRecipes: number; // -1 for unlimited
  maxCustomWaterProfiles: number;
  maxCustomFormulations: number;
  maxCustomSalts: number;
  maxStorageMB: number;
  maxApiCallsPerMonth: number;
  maxTeamMembers: number;
}

export interface UsageLimitCheck {
  resourceType: 'recipe' | 'water_profile' | 'formulation' | 'custom_salt';
  current: number;
  limit: number;
  canCreate: boolean;
  upgradeRequired?: SubscriptionTier;
}

export interface FeatureAccessCheck {
  feature: keyof FeatureFlags;
  hasAccess: boolean;
  requiredTier?: SubscriptionTier;
  upgradePrompt?: string;
}

export interface TrialInfo {
  isTrialing: boolean;
  trialEnd?: string;
  daysRemaining: number;
  trialUsed: boolean;
  eligibleForTrial: boolean;
}

export interface UpgradeRequest {
  targetTier: SubscriptionTier;
  billingCycle: 'monthly' | 'yearly';
  paymentMethodId?: string;
}

export interface DowngradeRequest {
  targetTier: SubscriptionTier;
  reason?: string;
  feedback?: string;
}

// Tier Configuration
export const TIER_FEATURES: Record<SubscriptionTier, FeatureFlags> = {
  [SubscriptionTier.FREE]: {
    versionHistory: false,
    csvImport: false,
    csvExport: false,
    teamCollaboration: false,
    advancedAnalytics: false,
    apiAccess: false,
    customBranding: false,
    prioritySupport: false
  },
  [SubscriptionTier.PROFESSIONAL]: {
    versionHistory: true,
    csvImport: true,
    csvExport: true,
    teamCollaboration: false,
    advancedAnalytics: true,
    apiAccess: false,
    customBranding: false,
    prioritySupport: true
  },
  [SubscriptionTier.ENTERPRISE]: {
    versionHistory: true,
    csvImport: true,
    csvExport: true,
    teamCollaboration: true,
    advancedAnalytics: true,
    apiAccess: true,
    customBranding: true,
    prioritySupport: true
  }
};

export const TIER_LIMITS: Record<SubscriptionTier, TierLimits> = {
  [SubscriptionTier.FREE]: {
    maxRecipes: 5,
    maxCustomWaterProfiles: 0,
    maxCustomFormulations: 2,
    maxCustomSalts: 0,
    maxStorageMB: 10,
    maxApiCallsPerMonth: 0,
    maxTeamMembers: 1
  },
  [SubscriptionTier.PROFESSIONAL]: {
    maxRecipes: 100,
    maxCustomWaterProfiles: 5,
    maxCustomFormulations: 20,
    maxCustomSalts: 10,
    maxStorageMB: 100,
    maxApiCallsPerMonth: 1000,
    maxTeamMembers: 5
  },
  [SubscriptionTier.ENTERPRISE]: {
    maxRecipes: -1, // unlimited
    maxCustomWaterProfiles: -1,
    maxCustomFormulations: -1,
    maxCustomSalts: -1,
    maxStorageMB: 1000,
    maxApiCallsPerMonth: 10000,
    maxTeamMembers: -1
  }
};
